import React, { useState, useEffect } from 'react';
import { Box, Card, Container, Group, Title, Text, Button, Badge, Table, ActionIcon, Modal, Select, TextInput, Textarea, NumberInput, Stack, Image, ScrollArea } from '@mantine/core';
import { IconEdit, IconTrash, IconPlus, IconUpload } from '@tabler/icons-react';

export default function RentalsTab({ user, apiBaseUrl, t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/rentals/my-items?ownerEmail=${encodeURIComponent(user.email)}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [user.email]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirm_delete') || 'Are you sure you want to delete this item?')) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/rentals/my-items/${id}?ownerEmail=${encodeURIComponent(user.email)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchItems();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/rentals/my-items/${editingItem.id}?ownerEmail=${encodeURIComponent(user.email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingItem)
      });
      if (res.ok) {
        setEditModalOpened(false);
        fetchItems();
      } else {
        alert('Failed to save item');
      }
    } catch (e) {
      console.error(e);
      alert('Error saving item');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEditingItem({ ...editingItem, imageBase64: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <ScrollArea h="100%">
      <Container size="xl" p="md">
        <Group justify="space-between" mb="lg">
          <Title order={3}>{t('rentals_title') || 'Marketplace / Wypożyczalnia'}</Title>
          <Button component="a" href="https://rent-tool.world" target="_blank" variant="light" color="indigo" leftSection={<IconPlus size={16} />}>
            Dodaj nowe ogłoszenie (Kreator AI)
          </Button>
        </Group>

        <Card withBorder shadow="sm" radius="md">
          {loading ? (
            <Text c="dimmed">Ładowanie...</Text>
          ) : items.length === 0 ? (
            <Text c="dimmed">Nie masz jeszcze żadnych ogłoszeń w Wypożyczalni.</Text>
          ) : (
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Zdjęcie</Table.Th>
                  <Table.Th>Nazwa</Table.Th>
                  <Table.Th>Cena (Doba)</Table.Th>
                  <Table.Th>Kaucja</Table.Th>
                  <Table.Th>Prowizja (%)</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Akcje</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map(it => (
                  <Table.Tr key={it.id}>
                    <Table.Td>{it.id}</Table.Td>
                    <Table.Td>
                      {it.imageUrl ? <Image src={it.imageUrl} w={40} h={40} radius="sm" fit="cover" /> : 
                       (it.mediaUrls && it.mediaUrls !== '[]') ? <Text size="xs">Galeria</Text> : <Text size="xs" c="dimmed">Brak</Text>}
                    </Table.Td>
                    <Table.Td fw={500}>{it.name}</Table.Td>
                    <Table.Td>{it.pricePerDay} {it.currency || "PLN"}</Table.Td>
                    <Table.Td>{it.depositPrice || 0} {it.currency || "PLN"}</Table.Td>
                    <Table.Td>{it.commissionRate || 2}%</Table.Td>
                    <Table.Td>
                      {it.confirmed ? (
                        <Badge color="green" variant="light">Opublikowane</Badge>
                      ) : (
                        <Badge color="orange" variant="light">Weryfikacja/Płatność</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon variant="light" color="blue" onClick={() => { setEditingItem(it); setEditModalOpened(true); }}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" onClick={() => handleDelete(it.id)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Container>

      <Modal opened={editModalOpened} onClose={() => setEditModalOpened(false)} title="Edytuj Ogłoszenie (Wypożyczalnia)" size="lg">
        {editingItem && (
          <Stack gap="md">
            <TextInput label="Nazwa" value={editingItem.name} onChange={e => setEditingItem({...editingItem, name: e.target.value})} />
            <Textarea label="Opis" minRows={4} value={editingItem.description} onChange={e => setEditingItem({...editingItem, description: e.target.value})} />
            <Textarea label="Protokół odbioru - znane uszkodzenia / stan techniczny (opcjonalnie)" placeholder="np. rysy na obudowie, brakujące akcesoria... (informacja trafi do e-maila po rezerwacji)" minRows={3} value={editingItem.conditionNotes || ''} onChange={e => setEditingItem({...editingItem, conditionNotes: e.target.value})} />
            <Group grow>
              <NumberInput label="Cena / Doba" value={editingItem.pricePerDay} onChange={v => setEditingItem({...editingItem, pricePerDay: v})} />
              <Select label="Waluta" data={['PLN', 'USD', 'EUR', 'GBP', 'CHF']} value={editingItem.currency || 'PLN'} onChange={v => setEditingItem({...editingItem, currency: v})} />
              <NumberInput label="Kaucja" value={editingItem.depositPrice || 0} onChange={v => setEditingItem({...editingItem, depositPrice: v})} />
              <NumberInput label="Prowizja (%)" value={editingItem.commissionRate || 2} onChange={v => setEditingItem({...editingItem, commissionRate: v})} />
            </Group>
            
            <Box>
              <Text size="sm" fw={500} mb={5}>Zdjęcie główne</Text>
              {(editingItem.imageBase64 || editingItem.imageUrl) && (
                <Image src={editingItem.imageBase64 || editingItem.imageUrl} w={150} h={150} radius="md" fit="cover" mb="xs" />
              )}
              <Button component="label" variant="outline" color="indigo" leftSection={<IconUpload size={16} />}>
                Wgraj nowe zdjęcie
                <input type="file" style={{ display: 'none' }} accept="image/*" onChange={handleImageUpload} />
              </Button>
            </Box>

            <Button fullWidth mt="md" color="indigo" onClick={handleSave}>Zapisz zmiany</Button>
          </Stack>
        )}
      </Modal>
    </ScrollArea>
  );
}
